// client/src/pages/admin/ManageGifts.js
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api.js';
import Modal from '../../components/ui/Modal.js';
import Button from '../../components/ui/Button.js';

// ─── Helpers ──────────────────────────────────────────────

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function formatCurrency(cents) {
  if (!cents && cents !== 0) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

function StatusBadge({ status }) {
  const styles = {
    pending_send: 'bg-amber-900/40 text-amber-400',
    sent: 'bg-blue-900/40 text-blue-400',
    redeemed: 'bg-green-900/40 text-green-400',
    expired: 'bg-royal-700 text-royal-200',
    abandoned: 'bg-red-900/40 text-red-400',
  };

  const labels = {
    pending_send: 'Incomplete',
    sent: 'Awaiting Redemption',
    redeemed: 'Redeemed',
    expired: 'Expired',
    abandoned: 'Abandoned',
  };

  return (
    <span className={`px-2 py-0.5 rounded-full text-sm font-medium ${styles[status] || 'bg-royal-700 text-royal-200'}`}>
      {labels[status] || status}
    </span>
  );
}

// ─── Main Component ──────────────────────────────────────

export default function ManageGifts() {
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Detail modal
  const [selectedGift, setSelectedGift] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  // Edit modal
  const [editGift, setEditGift] = useState(null);
  const [editStatus, setEditStatus] = useState('');
  const [editCodeExpiry, setEditCodeExpiry] = useState('');
  const [saving, setSaving] = useState(false);

  // Actions
  const [resending, setResending] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);

  const fetchGifts = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('limit', '30');
      if (statusFilter) params.set('status', statusFilter);
      if (search) params.set('search', search);

      const data = await api.get(`/gifts/admin/all?${params.toString()}`);
      setGifts(data.gifts || []);
      setPagination({ page: data.page, totalPages: data.totalPages, total: data.total });
    } catch (err) {
      console.error('Fetch gifts error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGifts();
  }, [statusFilter, search]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const handlePageChange = (newPage) => {
    fetchGifts(newPage);
  };

  // Open detail modal
  const openDetail = (gift) => {
    setSelectedGift(gift);
    setShowDetail(true);
  };

  // Open edit modal
  const openEdit = (gift) => {
    setEditGift(gift);
    setEditStatus(gift.status);
    setEditCodeExpiry(gift.giftcodeexpiresat ? new Date(gift.giftcodeexpiresat).toISOString().slice(0, 16) : '');
  };

  const handleSaveEdit = async () => {
    if (!editGift) return;
    setSaving(true);
    try {
      const updates = {};
      if (editStatus !== editGift.status) updates.status = editStatus;
      if (editCodeExpiry) updates.giftcodeexpiresat = new Date(editCodeExpiry).toISOString();

      await api.put(`/gifts/admin/${editGift.giftid}`, updates);
      setEditGift(null);
      setActionMessage({ type: 'success', text: 'Gift updated successfully' });
      fetchGifts(pagination.page);
    } catch (err) {
      setActionMessage({ type: 'error', text: err.data?.error || 'Failed to update gift' });
    } finally {
      setSaving(false);
    }
  };

  const handleResend = async (giftid) => {
    setResending(giftid);
    try {
      await api.post(`/gifts/admin/${giftid}/resend`);
      setActionMessage({ type: 'success', text: 'Gift email resent' });
    } catch (err) {
      setActionMessage({ type: 'error', text: err.data?.error || 'Failed to resend' });
    } finally {
      setResending(null);
    }
  };

  // Clear action message after 4 seconds
  useEffect(() => {
    if (actionMessage) {
      const timer = setTimeout(() => setActionMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [actionMessage]);

  // Stats
  const statusCounts = gifts.reduce((acc, g) => {
    acc[g.status] = (acc[g.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Dashboard Link */}
      <div className="mb-5">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gold text-royal-900 font-semibold text-base hover:bg-gold-light transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Dashboard
        </Link>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl text-gold">Gift Subscriptions</h1>
          <p className="text-royal-200 text-sm mt-1">{pagination.total} total gift{pagination.total !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Action message */}
      {actionMessage && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${
          actionMessage.type === 'success' ? 'bg-green-900/30 border border-green-700 text-green-400' : 'bg-red-900/30 border border-red-700 text-red-400'
        }`}>
          {actionMessage.text}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2.5 bg-royal-800/60 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50"
        >
          <option value="">All Statuses</option>
          <option value="pending_send">Incomplete</option>
          <option value="sent">Awaiting Redemption</option>
          <option value="redeemed">Redeemed</option>
          <option value="expired">Expired</option>
          <option value="abandoned">Abandoned</option>
        </select>

        <form onSubmit={handleSearch} className="flex gap-2 flex-1">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by email, sender, or gift code..."
            className="flex-1 px-3 py-2.5 bg-royal-800/60 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50"
          />
          <button type="submit" className="px-4 py-2.5 bg-royal-800/60 border border-royal-600 rounded-lg text-royal-200 hover:text-gold hover:border-gold/50 transition-colors text-base">
            Search
          </button>
          {search && (
            <button
              type="button"
              onClick={() => { setSearch(''); setSearchInput(''); }}
              className="px-3 py-2.5 text-royal-200 hover:text-white transition-colors text-base"
            >
              Clear
            </button>
          )}
        </form>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
        </div>
      ) : gifts.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-royal-200">No gifts found.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-royal-700">
                <th className="text-left text-royal-200 text-sm uppercase tracking-wider py-3 pr-3">Recipient</th>
                <th className="text-left text-royal-200 text-sm uppercase tracking-wider py-3 px-3">Plan</th>
                <th className="text-left text-royal-200 text-sm uppercase tracking-wider py-3 px-3">Buyer</th>
                <th className="text-center text-royal-200 text-sm uppercase tracking-wider py-3 px-3">Amount</th>
                <th className="text-center text-royal-200 text-sm uppercase tracking-wider py-3 px-3">Status</th>
                <th className="text-center text-royal-200 text-sm uppercase tracking-wider py-3 px-3">Created</th>
                <th className="text-right text-royal-200 text-sm uppercase tracking-wider py-3 pl-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {gifts.map((gift) => (
                <tr key={gift.giftid} className="border-b border-royal-700/30 hover:bg-royal-800/20 transition-colors">
                  <td className="py-3 pr-3">
                    <p className="text-white text-sm truncate max-w-[160px]">
                      {gift.recipientname || gift.recipientemail || '—'}
                    </p>
                    {gift.recipientname && gift.recipientemail && (
                      <p className="text-royal-200 text-xs truncate max-w-[160px]">{gift.recipientemail}</p>
                    )}
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-white text-sm">{gift.planname}</p>
                    <p className="text-royal-200 text-xs">{gift.giftinterval}</p>
                  </td>
                  <td className="py-3 px-3">
                    <p className="text-white text-sm truncate max-w-[140px]">
                      {gift.buyerfirstname || gift.buyeremail}
                    </p>
                    {gift.buyerfirstname && (
                      <p className="text-royal-200 text-xs truncate max-w-[140px]">{gift.buyeremail}</p>
                    )}
                  </td>
                  <td className="py-3 px-3 text-center">
                    <span className="text-gold text-sm font-medium">{formatCurrency(gift.amount)}</span>
                  </td>
                  <td className="py-3 px-3 text-center">
                    <StatusBadge status={gift.status} />
                  </td>
                  <td className="py-3 px-3 text-center">
                    <span className="text-royal-200 text-sm">{formatDate(gift.createdat)}</span>
                  </td>
                  <td className="py-3 pl-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openDetail(gift)}
                        className="text-blue-400 text-sm hover:text-blue-300 transition-colors"
                      >
                        View
                      </button>
                      <button
                        onClick={() => openEdit(gift)}
                        className="text-gold text-sm hover:text-gold-light transition-colors"
                      >
                        Edit
                      </button>
                      {gift.status === 'sent' && gift.recipientemail && (
                        <button
                          onClick={() => handleResend(gift.giftid)}
                          disabled={resending === gift.giftid}
                          className="text-green-400 text-sm hover:text-green-300 transition-colors disabled:opacity-60"
                        >
                          {resending === gift.giftid ? '...' : 'Resend'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page <= 1}
            className="text-base text-royal-200 hover:text-gold disabled:opacity-60 transition-colors"
          >
            ← Prev
          </button>
          <span className="text-royal-200 text-sm">Page {pagination.page} of {pagination.totalPages}</span>
          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page >= pagination.totalPages}
            className="text-base text-royal-200 hover:text-gold disabled:opacity-60 transition-colors"
          >
            Next →
          </button>
        </div>
      )}

      {/* Detail Modal */}
      <Modal isOpen={showDetail} onClose={() => setShowDetail(false)} title="Gift Details" maxWidth="max-w-lg">
        {selectedGift && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-royal-200 text-sm">Gift Code</p>
                <p className="text-gold font-mono font-bold select-all">{selectedGift.giftcode}</p>
              </div>
              <div>
                <p className="text-royal-200 text-sm">Status</p>
                <StatusBadge status={selectedGift.status} />
              </div>
              <div>
                <p className="text-royal-200 text-sm">Plan</p>
                <p className="text-white">{selectedGift.planname} ({selectedGift.giftinterval})</p>
              </div>
              <div>
                <p className="text-royal-200 text-sm">Amount</p>
                <p className="text-gold font-medium">{formatCurrency(selectedGift.amount)}</p>
              </div>
              <div>
                <p className="text-royal-200 text-sm">Duration</p>
                <p className="text-white">{selectedGift.giftdurationdays} days</p>
              </div>
              <div>
                <p className="text-royal-200 text-sm">Stripe Payment</p>
                <p className="text-white text-sm truncate">{selectedGift.stripepaymentintentid || '—'}</p>
              </div>
            </div>

            <div className="border-t border-royal-700/50 pt-4">
              <h3 className="text-white font-medium mb-2">Buyer</h3>
              <p className="text-white text-sm">{selectedGift.buyerfirstname} {selectedGift.buyerlastname}</p>
              <p className="text-royal-200 text-sm">{selectedGift.buyeremail}</p>
            </div>

            <div className="border-t border-royal-700/50 pt-4">
              <h3 className="text-white font-medium mb-2">Recipient</h3>
              <p className="text-white text-sm">{selectedGift.recipientname || '—'}</p>
              <p className="text-royal-200 text-sm">{selectedGift.recipientemail || 'Not set'}</p>
              {selectedGift.recipientaccountemail && (
                <p className="text-green-400 text-sm mt-1">Account: {selectedGift.recipientaccountemail}</p>
              )}
            </div>

            <div className="border-t border-royal-700/50 pt-4">
              <h3 className="text-white font-medium mb-2">Timeline</h3>
              <div className="space-y-1.5 text-sm">
                <p className="text-royal-200">Created: <span className="text-white">{formatDateTime(selectedGift.createdat)}</span></p>
                <p className="text-royal-200">Sent: <span className="text-white">{formatDateTime(selectedGift.sentat)}</span></p>
                <p className="text-royal-200">Code Expires: <span className="text-white">{formatDateTime(selectedGift.giftcodeexpiresat)}</span></p>
                <p className="text-royal-200">Redeemed: <span className="text-white">{formatDateTime(selectedGift.redeemedat)}</span></p>
                <p className="text-royal-200">Gift Expires: <span className="text-white">{formatDateTime(selectedGift.giftexpiresat)}</span></p>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={!!editGift} onClose={() => setEditGift(null)} title="Edit Gift">
        {editGift && (
          <div className="space-y-4">
            <div className="bg-royal-800/40 rounded-lg p-3">
              <p className="text-royal-200 text-sm">Gift Code</p>
              <p className="text-gold font-mono font-bold">{editGift.giftcode}</p>
              <p className="text-royal-200 text-sm mt-1">{editGift.planname} · {formatCurrency(editGift.amount)}</p>
            </div>

            <div>
              <label className="block text-base text-white mb-1.5">Status</label>
              <select
                value={editStatus}
                onChange={(e) => setEditStatus(e.target.value)}
                className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50"
              >
                <option value="pending_send">Incomplete</option>
                <option value="sent">Awaiting Redemption</option>
                <option value="redeemed">Redeemed</option>
                <option value="expired">Expired</option>
                <option value="abandoned">Abandoned</option>
              </select>
              {editStatus === 'expired' && editGift.status === 'redeemed' && (
                <p className="text-amber-400 text-sm mt-1.5">
                  Warning: Marking as expired will downgrade the recipient to the Free plan.
                </p>
              )}
            </div>

            <div>
              <label className="block text-base text-white mb-1.5">Code Expiry Date</label>
              <input
                type="datetime-local"
                value={editCodeExpiry}
                onChange={(e) => setEditCodeExpiry(e.target.value)}
                className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50"
              />
              <p className="text-royal-200 text-sm mt-1">Extend or shorten the redemption deadline.</p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button onClick={handleSaveEdit} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button variant="outline" onClick={() => setEditGift(null)}>Cancel</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}